import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import PersonClient from "components/PersonClient";
import clientService from "api/clientService";
import ClientsContext from "contexts/ClientsContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { IClient } from "dto/IClient";
import InfoBoxClient from "components/InfoBoxClient";
import CasesClient from "components/CasesClient";
import PersonsClient from "components/PersonsClient";
import ModalsManagerContext from "contexts/ModalsManager";
import NoteIcon from "assets/icons/note.svg?react";
import PersonIcon from "assets/icons/company/person.svg?react";
import CaseIcon from "assets/icons/case.svg?react";
import DocIcon from "assets/icons/letters.svg?react";
import HistoryIcon from "assets/icons/history.svg?react";
import NotesDesk from "components/NotesDesk";
import ClientVersionsHistory from "components/ClientVersionsHistory";
import FilesClient from "components/FilesClient";
import usePDF from "hooks/usePDF";
import { IAttachment } from "dto/IAttachment";
import PDFViewer from "components/PDFViewer";
import { ItemKind } from "dto/IKindItems";
import classNames from "classnames";
import { Can } from "contexts/AbilityContext";
import { useTranslation } from "react-i18next";

const ClientRightSide: FC<Props> = ({ clientId }) => {
  const { t } = useTranslation();
  const { data: clientData } = clientService.useGetClient(clientId ?? "");
  const {
    blob,
    create,
    removeBlob,
    attachment,
  } = usePDF();

  const { modals, setModals } = useContext(ModalsManagerContext);
  const [showCreateNote, setShowCreateNote] = useState<boolean>(false);

  //@ts-ignore
  const client: IClient = useMemo(() => clientData?.data ?? [], [clientData]);

  const { clients } = useContext(ClientsContext);

  const clientContext = useMemo(
    () => clients.find((c) => c.Id === client.Id) ?? [],
    [client, clients],
  );

  // Odcisk danych podmiotu z listy (edycje aktualizują ją w miejscu) — po zapisie zmian historia
  // pobiera się ponownie i pokazuje nową wersję.
  const historyRevision = useMemo(() => {
    const current = clientContext as IClient;
    return JSON.stringify([
      current?.Name,
      current?.NIP,
      current?.REGON,
      current?.Data,
    ]);
  }, [clientContext]);

  const [uploadFileTrigger, setUploadFileTrigger] = useState<number>(0);
  const pdfRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (pdfRef.current) {
      console.log("adding show class");
      pdfRef.current.classList.add(styles.show);
    }
  }, [pdfRef.current]);

  if (!clientContext) {
    return <></>;
  }

  const handleClickNewCase = () => {
    setModals({ ...modals, createCase: true, hide: false });
  };

  const handleClickNewEntity = () => {
    setModals({ ...modals, addClient: true, hide: false });
  };

  const handleClickNewFile = () => {
    setUploadFileTrigger(uploadFileTrigger + 1);
  };

  const onPreview = (a: IAttachment) => {
    create(null, a, ItemKind.CaseFile);
  };

  return (
    <div
      className={classNames(styles.wrapper, blob ? styles.withpreview : null)}
    >
      <div className={styles.person}>
        <PersonClient client={clientContext as IClient} />
      </div>
      <div className={styles.informations}>
        <Can I="accessModule" an="notes">
          <InfoBoxClient
            createLabel="+ Nowa notatka"
            label={
              <div className={styles.label}>
                <NoteIcon /> Notatki
              </div>
            }
            onCreateClick={() => setShowCreateNote(true)}
          >
            <NotesDesk
              inGrid={false}
              showHeader={false}
              participantId={client.Id}
              showCreateNoteExternal={showCreateNote}
              setShowCreateNoteExternal={setShowCreateNote}
            ></NotesDesk>
          </InfoBoxClient>
        </Can>
        {/* <InfoBoxClient
          createLabel="+ Nowy adres"
          label={
            <div className={styles.label}>
              <AddressIcon /> Adres
            </div>
          }
          onCreateClick={handleClickNewAddress}
        >
          <AddressClient client={client} />
        </InfoBoxClient> */}
        <Can I="accessModule" an="cases">
          <InfoBoxClient
            createLabel="+ Nowa teczka"
            label={
              <div className={styles.label}>
                <CaseIcon /> Teczki
              </div>
            }
            onCreateClick={handleClickNewCase}
          >
            <CasesClient />
          </InfoBoxClient>
        </Can>

        <Can I="accessModule" an="drive">
          <InfoBoxClient
            createLabel={`+ ${t("clients.newPowerOfAttorney")}`}
            label={
              <div className={styles.label}>
                <DocIcon /> {t("clients.powersOfAttorney")}
              </div>
            }
            onCreateClick={handleClickNewFile}
          >
            <FilesClient
              uploadTrigger={uploadFileTrigger}
              dismissUploadTrigger={() => setUploadFileTrigger(0)}
              onPreview={onPreview}
            />
          </InfoBoxClient>
        </Can>
        <InfoBoxClient
          // createLabel="+ Nowy podmiot"
          label={
            <div className={styles.label}>
              <PersonIcon /> Kontakty
            </div>
          }
          onCreateClick={handleClickNewEntity}
        >
          <PersonsClient client={client} />
        </InfoBoxClient>
        <InfoBoxClient
          label={
            <div className={styles.label}>
              <HistoryIcon /> {t("clients.history.title")}
            </div>
          }
          onCreateClick={() => undefined}
        >
          <ClientVersionsHistory
            clientId={clientId}
            revision={historyRevision}
          />
        </InfoBoxClient>
      </div>

      {blob && (
        <div className={styles.PDFWrapper} ref={pdfRef}>
          <PDFViewer
            showTitle
            file={blob}
            title={
              attachment?.Label ? attachment.Label : attachment?.FileName ?? ""
            }
            showHeader={true}
            onCloseButton={() => {
              pdfRef.current.classList.remove(styles.show);
              pdfRef.current.classList.add(styles.hide);
              setTimeout(() => {
                removeBlob();
              }, 1000);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ClientRightSide;
